import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  X, Plus, Check, Pencil, Trash2,
  Wifi, Snowflake, UtensilsCrossed, WashingMachine, Wind, Tv, Waves,
  Bath as BathIcon, Car, Dumbbell, ArrowUpDown, Fence, Umbrella, PawPrint, Coffee,
  Sparkles, Shirt, Flame, ShieldCheck, Laptop,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AMENITY_ICONS = {
  'wifi': Wifi, 'snowflake': Snowflake, 'utensils-crossed': UtensilsCrossed,
  'washing-machine': WashingMachine, 'wind': Wind, 'tv': Tv, 'waves': Waves,
  'bath': BathIcon, 'car': Car, 'dumbbell': Dumbbell, 'arrow-up-down': ArrowUpDown,
  'fence': Fence, 'umbrella': Umbrella, 'paw-print': PawPrint, 'coffee': Coffee,
  'sparkles': Sparkles, 'shirt': Shirt, 'flame': Flame, 'shield-check': ShieldCheck,
  'laptop': Laptop,
};

export function AmenityIcon({ icon, className = 'h-5 w-5' }) {
  const IconComp = AMENITY_ICONS[icon] || Sparkles;
  return <IconComp className={className} />;
}

const AMENITY_ICON_CHOICES = Object.keys(AMENITY_ICONS);

export function AmenityPicker({ open, onOpenChange, currentAmenities, onSave, adminPass, zIndex = 60 }) {
  const [defaults, setDefaults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [customName, setCustomName] = useState('');
  const [editingDefaults, setEditingDefaults] = useState(false);
  const [draftDefaults, setDraftDefaults] = useState([]);
  const [newDefaultName, setNewDefaultName] = useState('');
  const [newDefaultIcon, setNewDefaultIcon] = useState('sparkles');
  const [savingDefaults, setSavingDefaults] = useState(false);

  const loadDefaults = () => fetch(`${API}/api/public/amenities/defaults`).then(r => r.json()).then(setDefaults).catch(() => {});
  useEffect(() => { loadDefaults(); }, []);
  useEffect(() => { if (open) { setSelected(currentAmenities.map(a => ({ ...a }))); setEditingDefaults(false); } }, [open, currentAmenities]);

  const isSelected = (name) => selected.some(s => s.name === name);
  const toggleDefault = (amenity) => {
    if (editingDefaults) return;
    if (isSelected(amenity.name)) setSelected(prev => prev.filter(s => s.name !== amenity.name));
    else setSelected(prev => [...prev, { name: amenity.name, icon: amenity.icon }]);
  };
  const addCustom = () => {
    const name = customName.trim();
    if (!name || isSelected(name)) return;
    setSelected(prev => [...prev, { name, icon: 'sparkles' }]);
    setCustomName('');
  };

  const startEditDefaults = () => { setDraftDefaults(defaults.map(a => ({ ...a }))); setEditingDefaults(true); };
  const cancelEditDefaults = () => { setEditingDefaults(false); setNewDefaultName(''); };
  const removeDraft = (name) => setDraftDefaults(d => d.filter(a => a.name !== name));
  const renameDraft = (oldName, newName) => {
    const n = (newName || '').trim();
    if (!n) return;
    if (draftDefaults.some(a => a.name.toLowerCase() === n.toLowerCase() && a.name !== oldName)) {
      toast.error('An amenity with that name already exists');
      return;
    }
    setDraftDefaults(d => d.map(a => a.name === oldName ? { ...a, name: n } : a));
  };
  const changeDraftIcon = (name, icon) => setDraftDefaults(d => d.map(a => a.name === name ? { ...a, icon } : a));
  const addDraft = () => {
    const n = newDefaultName.trim();
    if (!n) return;
    if (draftDefaults.some(a => a.name.toLowerCase() === n.toLowerCase())) {
      toast.error('Already in the list');
      return;
    }
    setDraftDefaults(d => [...d, { name: n, icon: newDefaultIcon }]);
    setNewDefaultName('');
    setNewDefaultIcon('sparkles');
  };
  const saveDefaults = async () => {
    setSavingDefaults(true);
    try {
      const res = await fetch(`${API}/api/public/admin/amenities/defaults`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPass, amenities: draftDefaults }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      const keptNames = new Set(data.amenities.map(a => a.name.toLowerCase()));
      setSelected(prev => prev.filter(s => keptNames.has(s.name.toLowerCase()) || !defaults.some(d => d.name === s.name)));
      setDefaults(data.amenities);
      setEditingDefaults(false);
      toast.success('Default amenities updated');
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSavingDefaults(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" style={{ zIndex }} data-testid="amenity-picker-dialog">
        <DialogHeader><DialogTitle className="font-heading">Manage Amenities</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Common Amenities</Label>
              {adminPass && !editingDefaults && (
                <button onClick={startEditDefaults} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="amenity-edit-defaults-btn">
                  <Pencil className="h-3 w-3" /> Edit defaults
                </button>
              )}
              {editingDefaults && (
                <div className="flex items-center gap-2">
                  <button onClick={cancelEditDefaults} className="text-xs text-muted-foreground hover:text-foreground" data-testid="amenity-cancel-defaults-btn">Cancel</button>
                  <Button size="sm" className="h-6 text-xs" onClick={saveDefaults} disabled={savingDefaults} data-testid="amenity-save-defaults-btn">
                    {savingDefaults ? 'Saving…' : 'Save defaults'}
                  </Button>
                </div>
              )}
            </div>

            {editingDefaults ? (
              <>
                <div className="grid grid-cols-1 gap-1.5" data-testid="amenity-defaults-edit-list">
                  {draftDefaults.map(a => (
                    <DefaultAmenityEditRow
                      key={a.name}
                      amenity={a}
                      onRename={(n) => renameDraft(a.name, n)}
                      onChangeIcon={(icon) => changeDraftIcon(a.name, icon)}
                      onRemove={() => removeDraft(a.name)}
                    />
                  ))}
                </div>
                <div className="flex gap-2 mt-3 pt-3 border-t border-border/30">
                  <Select value={newDefaultIcon} onValueChange={setNewDefaultIcon}>
                    <SelectTrigger className="h-8 w-32 text-xs" data-testid="amenity-new-icon"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {AMENITY_ICON_CHOICES.map(i => (
                        <SelectItem key={i} value={i}>
                          <span className="inline-flex items-center gap-2"><AmenityIcon icon={i} className="h-3.5 w-3.5" />{i}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input value={newDefaultName} onChange={e => setNewDefaultName(e.target.value)} placeholder="New default amenity…" className="h-8 text-xs flex-1" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addDraft(); } }} data-testid="amenity-new-name" />
                  <Button variant="outline" size="sm" className="h-8" onClick={addDraft} disabled={!newDefaultName.trim()} data-testid="amenity-add-default-btn"><Plus className="h-3.5 w-3.5" /></Button>
                </div>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {defaults.map(amenity => (
                  <button key={amenity.name} onClick={() => toggleDefault(amenity)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-colors ${isSelected(amenity.name) ? 'bg-primary/10 border-primary text-primary' : 'border-border hover:bg-muted'}`}>
                    <AmenityIcon icon={amenity.icon} className="h-4 w-4 flex-shrink-0" />
                    <span className="flex-1">{amenity.name}</span>
                    {isSelected(amenity.name) && <Check className="h-4 w-4" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {!editingDefaults && (
            <>
              <div>
                <Label className="text-sm font-medium mb-2 block">Add Custom Amenity</Label>
                <div className="flex gap-2">
                  <Input value={customName} onChange={e => setCustomName(e.target.value)} placeholder="e.g., Rooftop Access" onKeyDown={e => e.key === 'Enter' && addCustom()} />
                  <Button variant="outline" onClick={addCustom} disabled={!customName.trim()}><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
              {selected.filter(s => !defaults.some(d => d.name === s.name)).length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Custom Amenities</Label>
                  <div className="flex flex-wrap gap-2">
                    {selected.filter(s => !defaults.some(d => d.name === s.name)).map(s => (
                      <div key={s.name} className="flex items-center gap-1.5 bg-muted rounded-full px-3 py-1 text-xs">
                        <AmenityIcon icon={s.icon} className="h-3 w-3" /><span>{s.name}</span>
                        <button onClick={() => setSelected(prev => prev.filter(p => p.name !== s.name))} className="text-destructive hover:text-destructive/80 ml-0.5"><X className="h-3 w-3" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button onClick={() => { onSave(selected); onOpenChange(false); }}>Save Amenities ({selected.length})</Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DefaultAmenityEditRow({ amenity, onRename, onChangeIcon, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(amenity.name);
  useEffect(() => { setVal(amenity.name); }, [amenity.name]);
  return (
    <div className="flex items-center gap-1.5 border border-border/60 rounded-md px-2 py-1.5 bg-white" data-testid="amenity-default-row">
      <Select value={amenity.icon} onValueChange={onChangeIcon}>
        <SelectTrigger className="h-7 w-14 p-1 border-0 bg-transparent" data-testid="amenity-default-icon">
          <AmenityIcon icon={amenity.icon} className="h-4 w-4" />
        </SelectTrigger>
        <SelectContent className="max-h-64">
          {AMENITY_ICON_CHOICES.map(i => (
            <SelectItem key={i} value={i}>
              <span className="inline-flex items-center gap-2"><AmenityIcon icon={i} className="h-3.5 w-3.5" />{i}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {editing ? (
        <Input
          value={val}
          onChange={e => setVal(e.target.value)}
          onBlur={() => { setEditing(false); if (val.trim() && val !== amenity.name) onRename(val); else setVal(amenity.name); }}
          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setVal(amenity.name); setEditing(false); } }}
          className="h-7 text-xs flex-1"
          autoFocus
        />
      ) : (
        <button onClick={() => setEditing(true)} className="flex-1 text-left text-sm hover:text-primary" data-testid="amenity-default-name">{amenity.name}</button>
      )}
      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={onRemove} data-testid="amenity-default-remove-btn"><Trash2 className="h-3.5 w-3.5" /></Button>
    </div>
  );
}
